import React, { useState, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
} from "@mui/material";
import { styled } from "@mui/system";
import CustomSwitch from "./CustomSwitch";
import SwitchWithInput from "./SwitchWithInput";
import "./index.css";
import beforeQA from "../../assets/design-revamp-company/company-epr/before-EPR-QA.svg";
import afterQA from "../../assets/design-revamp-company/company-epr/after-EPR-QA.svg";

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  maxWidth: "64rem",
  margin: "0 auto",
  overflow: "hidden",
  padding: theme.spacing(2, 0),
}));

const CarouselContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const SlideContainer = styled(Box)(({ isActive, moreStyle }) => ({
  width: "33.333%",
  padding: "0 8px",
  transition: "all 0.3s ease-in-out",
  transform: isActive ? "scale(1)" : "scale(0.9)",
  opacity: isActive ? 1 : 0.7,
  zIndex: isActive ? 10 : 5,
  ...moreStyle,
}));

const IndicatorContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  marginTop: "16px",
});

const IndicatorButton = styled(Button)(({ isActive, theme }) => ({
  height: "4px",
  width: "32px",
  margin: "0 4px",
  transition: "all 0.3s",
  backgroundColor: isActive ? "#00376F" : "rgba(0, 55, 111, 0.3)",
  "&:hover": {
    backgroundColor: "rgba(0, 55, 111, 0.6)",
  },
  "&:focus": {
    outline: "none",
    boxShadow: `0 0 0 2px #00376F`,
  },
}));

const InteractiveCarousel = ({
  items = [],
  classNameCard,
  typographyColor,
  isTitle,
  titleColor,
  answers,
  setAnswers,
  onSubmit,
  loading,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const goToSlide = useCallback(
    (index) => {
      if (index >= 0 && index < items.length) {
        setActiveIndex(index);
      }
    },
    [items.length]
  );

  const goToNextSlide = useCallback(() => {
    goToSlide(activeIndex + 1);
  }, [activeIndex, goToSlide]);

  const goToPrevSlide = useCallback(() => {
    goToSlide(activeIndex - 1);
  }, [activeIndex, goToSlide]);

  const getVisibleSlides = useCallback(() => {
    if (activeIndex === 0) {
      return [0, 1];
    } else if (activeIndex === items.length - 1) {
      return [-1, 0];
    }
    return [-1, 0, 1];
  }, [activeIndex, items.length]);

  const getItemIndex = useCallback(
    (index) => {
      return items.length ? (index + items.length) % items.length : 0;
    },
    [items.length]
  );

  const handleAnswerChange = useCallback(
    (itemId, questionIndex, value, inputValue = null) => {
      setAnswers((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [questionIndex]: {
            value: value,
            inputValue: inputValue,
          },
        },
      }));
    },
    [setAnswers]
  );

  const renderQuestion = useCallback(
    (item, question, index) => {
      const answer = answers[item.id]?.[index] || {};

      switch (question.type) {
        case "switch":
          return (
            <CustomSwitch
              options={question.options}
              value={answer.value || question.options[1]}
              onChange={(value) => handleAnswerChange(item.id, index, value)}
            />
          );
        case "input":
          return (
            <div className="clipped-textfield">
              <TextField
                value={answer.value || ""}
                onChange={(e) =>
                  handleAnswerChange(item.id, index, e.target.value)
                }
                InputProps={{
                  endAdornment: question.unit,
                }}
              />
            </div>
          );
        case "switchWithInput":
          return (
            <SwitchWithInput
              value={answer.value || "No"}
              onChange={(value) =>
                handleAnswerChange(item.id, index, value, answer.inputValue)
              }
              inputValue={answer.inputValue || ""}
              onInputChange={(inputValue) =>
                handleAnswerChange(
                  item.id,
                  index,
                  answer.value || "No",
                  inputValue
                )
              }
            />
          );
        case "multiInput":
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
                width: "100%",
                minWidth: "300px",
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              {question.inputs.map((input, inputIndex) => (
                <div key={inputIndex} className="multiinput-container">
                  <div className={"multi-textfield active"}>
                    <TextField isActive={true} value={input.label} disabled />
                  </div>
                  <div className="multi-textfield">
                    <TextField
                      value={
                        Array.isArray(answer.value)
                          ? answer.value[inputIndex] || "0"
                          : "0"
                      }
                      onChange={(e) => {
                        const newMultiInputAnswers = Array.isArray(answer.value)
                          ? [...answer.value]
                          : [];
                        newMultiInputAnswers[inputIndex] = e.target.value;
                        handleAnswerChange(
                          item.id,
                          index,
                          newMultiInputAnswers
                        );
                      }}
                      InputProps={{
                        endAdornment: input.unit,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          );
        default:
          return null;
      }
    },
    [answers, handleAnswerChange]
  );

  const memoizedContent = useMemo(() => {
    const visibleSlides = getVisibleSlides();

    return visibleSlides.map((offset) => {
      const index = getItemIndex(activeIndex + offset);
      const item = items[index];

      if (offset === -1) {
        return (
          <React.Fragment key={offset}>
            <SlideContainer
              isActive={false}
              moreStyle={
                offset === -1
                  ? { display: "flex", justifyContent: "flex-end" }
                  : {}
              }
            >
              <img
                src={beforeQA}
                alt="Before QA Card"
                width={70}
                height={129.51}
                onClick={() => goToPrevSlide()}
              />
            </SlideContainer>
          </React.Fragment>
        );
      }

      if (offset === 1) {
        return (
          <React.Fragment key={offset}>
            <SlideContainer isActive={false}>
              <img
                src={afterQA}
                alt="After QA Card"
                width={70}
                height={129.51}
                onClick={() => goToNextSlide()}
              />
            </SlideContainer>
          </React.Fragment>
        );
      }

      // For the active slide (offset === 0), render the full content
      return (
        <SlideContainer key={offset} isActive={true}>
          {isTitle && item?.title && (
            <Typography
              variant="h6"
              component="div"
              sx={{ color: titleColor, textAlign: "center" }}
            >
              {item.title}
            </Typography>
          )}
          {item && item.questions && item.questions.length > 0 ? (
            item.questions.map((question, qIndex) => (
              <Card
                key={qIndex}
                className={classNameCard}
                sx={{ marginBottom: "8px", minWidth: "fit-content" }}
              >
                <CardContent>
                  <Typography
                    variant="body2"
                    sx={{ color: typographyColor, marginBottom: "8px" }}
                  >
                    {question.content}
                  </Typography>
                  {renderQuestion(item, question, qIndex)}
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", padding: "20px" }}
            >
              No questions available for this item.
            </Typography>
          )}
        </SlideContainer>
      );
    });
  }, [
    activeIndex,
    items,
    isTitle,
    titleColor,
    classNameCard,
    typographyColor,
    renderQuestion,
    getItemIndex,
    getVisibleSlides,
    goToNextSlide,
    goToPrevSlide,
  ]);

  if (!items || items.length === 0) {
    return (
      <Typography variant="body1" sx={{ textAlign: "center", padding: "20px" }}>
        No items to display.
      </Typography>
    );
  }

  return (
    <div>
      <CarouselContainer>
        <CarouselContent>{memoizedContent}</CarouselContent>
        <IndicatorContainer>
          {items.map((_, index) => (
            <IndicatorButton
              key={index}
              onClick={() => goToSlide(index)}
              isActive={index === activeIndex}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </IndicatorContainer>
      </CarouselContainer>
      {activeIndex === items.length - 1 && (
        <Box sx={{ textAlign: "center" }}>
          <div style={{ margin: "0 auto", textAlign: "center" }}>
            <button
              className="submit-epr-button"
              onClick={onSubmit}
              disabled={loading === "loading"}
              style={{
                marginTop: "28px",
                border: "none",
                padding: "5px 10px",
                color: "white",
                fontSize: "16px",
              }}
            >
              Submit
            </button>
          </div>
        </Box>
      )}
    </div>
  );
};

export default InteractiveCarousel;
